import React from "react";
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import FlagEmoji from "../../../../components/common/FlagEmoji";
import {
  Description as DescriptionIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { FileText } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { useSearchTokens } from "../../../SearchScreen/searchDesignTokens";

const EmptyOption = ({
  contractName,
  setContractName,
  description,
  setDescription,
  error,
  selectedCountry,
  setSelectedCountry,
  onCountryChange,
}) => {
  const { t } = useTranslation();
  const dk = useSearchTokens();

  const countryOptions = [
    { value: 'MA', flag: '🇲🇦', name: 'Maroc' },
    { value: 'AE', flag: '🇦🇪', name: 'UAE' },
  ];

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dk.inputBg,
      borderRadius: '8px',
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr, borderWidth: 1 },
    },
    '& .MuiInputLabel-root': {
      color: dk.text3,
      fontSize: '0.8rem',
      '&.Mui-focused': { color: dk.gold },
    },
    '& .MuiSelect-select': { color: dk.text1 },
    '& .MuiSvgIcon-root': { color: dk.text3 },
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dk.inputBg,
      borderRadius: '8px',
      color: dk.text1,
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr, borderWidth: 1 },
    },
    '& .MuiInputLabel-root': {
      color: dk.text3,
      fontSize: '0.8rem',
      '&.Mui-focused': { color: dk.gold },
    },
    '& .MuiInputBase-input': { color: dk.text1 },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: dk.text3, fontSize: '1.1rem' },
    '& .MuiFormHelperText-root': { fontSize: '0.7rem' },
  };

  const sectionLabel = {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: dk.text3,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    mb: 1,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Header */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: '1.1rem',
            fontWeight: 500,
            color: dk.text1,
            mb: 0.5,
          }}
        >
          {t("empty_option_title") || "Create Empty Contract"}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: dk.text3, lineHeight: 1.6 }}>
          {t("empty_option_subtitle") || "Start with a blank document and add your content later"}
        </Typography>
      </Box>

      {/* Country */}
      <Box>
        <Typography sx={sectionLabel}>{t("select_country") || "Jurisdiction"}</Typography>
        <FormControl fullWidth size="small" sx={selectSx}>
          <InputLabel>{t("country") || "Country"}</InputLabel>
          <Select
            value={selectedCountry || ""}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              if (onCountryChange) onCountryChange(e.target.value);
            }}
            label={t("country") || "Country"}
            renderValue={(value) => {
              if (!value) return <em style={{ color: dk.text3 }}>{t("select_country_first") || "Select a country"}</em>;
              const c = countryOptions.find(o => o.value === value);
              return c ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagEmoji emoji={c.flag} size={18} />
                  <span>{c.name}</span>
                </Box>
              ) : value;
            }}
          >
            <MenuItem value=""><em>{t("select_country_first") || "Select a country"}</em></MenuItem>
            {countryOptions.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagEmoji emoji={c.flag} size={18} />
                  <span>{c.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Empty contract placeholder */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '10px',
          border: `1px dashed ${dk.border}`,
          backgroundColor: dk.elevated,
          minHeight: 160,
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            backgroundColor: dk.goldSub,
            border: `1px solid ${dk.goldBdr}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0.5,
          }}
        >
          <FileText size={20} color={dk.gold} />
        </Box>
        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: '1rem',
            fontWeight: 500,
            color: dk.text2,
          }}
        >
          {t("empty_contract") || "Empty Contract"}
        </Typography>
        <Typography sx={{ fontSize: '0.74rem', color: dk.text3, textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}>
          {t("empty_contract_description") || "You will start with a completely blank document."}
        </Typography>
      </Box>

      {/* Contract details */}
      <Box>
        <Typography sx={sectionLabel}>{t("contract_details") || "Contract Details"}</Typography>
        <TextField
          fullWidth
          size="small"
          label={t("contract_title") || "Contract Title"}
          value={contractName}
          onChange={(e) => setContractName(e.target.value)}
          error={!!error && !contractName.trim()}
          helperText={!contractName.trim() && error ? t("name_required") || "Contract name required" : ""}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <InsertDriveFileIcon />
              </InputAdornment>
            ),
          }}
          sx={{ ...textFieldSx, mb: 1.5 }}
        />
        <TextField
          fullWidth
          size="small"
          label={t("description_optional") || "Description (Optional)"}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DescriptionIcon />
              </InputAdornment>
            ),
          }}
          sx={textFieldSx}
        />
      </Box>
    </Box>
  );
};

export default EmptyOption;
